import { useEffect, useMemo, useState } from "react"
import { getSameDayPerformanceStaleness, IntradayPeriodStaleness } from "helpers/intraday"
import { useAppSelector } from "hooks/redux"
import { useDashboardId } from "../context/dashboard-id"
import { DEFAULT_CORE_STATE } from "../store/core-slice"
import { useDashboardModelName } from "./use-dashboard-model-name"

const NOT_STALE: IntradayPeriodStaleness = { isStale: false, tooltipLabel: "" }

// Fallback for when a period change's dates dispatch never lands (new dates equal old ones).
const SETTLE_FALLBACK_MS = 500

// Guard: dates and period are written in separate Redux commits — only evaluate once dates catch up.
export function useIntradayPeriodStaleness(): IntradayPeriodStaleness {
  const dashboardId = useDashboardId()
  const { period, performanceStartDate, performanceEndDate } = useAppSelector(
    (s) => s.dashboard.core[dashboardId] ?? DEFAULT_CORE_STATE,
  )
  const modelName = useDashboardModelName()

  const datesKey = `${performanceStartDate}|${performanceEndDate}`
  const [settled, setSettled] = useState({ datesKey, period })

  if (datesKey !== settled.datesKey) {
    setSettled({ datesKey, period })
  }

  const isSettled = period === settled.period

  useEffect(() => {
    if (isSettled) return undefined
    const timer = setTimeout(() => setSettled({ datesKey, period }), SETTLE_FALLBACK_MS)
    return () => clearTimeout(timer)
  }, [isSettled, datesKey, period])

  return useMemo(
    () => (
      isSettled
        ? getSameDayPerformanceStaleness(period, performanceStartDate, performanceEndDate, modelName)
        : NOT_STALE
    ),
    [isSettled, period, performanceStartDate, performanceEndDate, modelName],
  )
}
